import { KEY } from "src/consts/nexus-keys";
import { nexusStore } from "src/store/nexus";
import { initNexus, refetchNexus } from "./init";

/**
 * Live Nexus refetch controller.
 *
 * Keeps the post-auth Nexus store fresh mid-session so a rollout/rollback lands
 * WITHOUT a full page reload. Refetches are event-driven (tab focus, navigation —
 * wired in useNexusLiveRefetch), NOT interval-polled: an idle/backgrounded tab
 * generates zero traffic.
 *
 * The values below are the tuning knobs. See docs/technicals/trx-microservices-flag.md.
 */

// Dedupe FLOOR (not a polling interval): triggers may fire back-to-back (focus +
// navigation); this collapses bursts so we issue at most one /v1/variants GET per
// window per session. Rollback lands within ~this long of the operator touching the app.
//
// The value is Nexus-configurable (KEY.NEXUS_REFETCH_CONFIG → ttl_seconds) so ops can
// dial cadence up/down WITHOUT a deploy. This constant is only the fallback used before
// the first Nexus load resolves.
export const DEFAULT_NEXUS_STALE_TTL_MS = 60_000;

// Clamp the Nexus-provided value so a misconfiguration can't (a) set it near 0 and turn
// every trigger into a request storm, or (b) set it huge and strand rollback for hours.
const MIN_STALE_TTL_MS = 15_000; // 15s
const MAX_STALE_TTL_MS = 30 * 60_000; // 30m

// Read the current floor from the Nexus store each call, so a refetch that pulls a new
// ttl_seconds takes effect on the very next refetch. No chicken-and-egg: the initial
// load (loadNexus) is NOT TTL-gated, so a value is always present by the first refetch.
const getStaleTtlMs = (): number => {
  const seconds = nexusStore.getState().getNexusPayload(KEY.NEXUS_REFETCH_CONFIG)?.ttl_seconds;
  const ms = typeof seconds === "number" && seconds > 0 ? seconds * 1000 : DEFAULT_NEXUS_STALE_TTL_MS;
  return Math.min(Math.max(ms, MIN_STALE_TTL_MS), MAX_STALE_TTL_MS);
};

// Device id used until an authenticated company id arrives (matches App's bootstrap).
// Exported so consumers can tell an anon-scoped response apart from a company-scoped one.
export const ANON_COMPANY_ID = "ffb";

let currentCompanyId = ANON_COMPANY_ID;
let lastFetchedAt = 0;
let inFlight = false;
// Monotonic token: guards against out-of-order responses clobbering newer state
// (e.g. the anon "ffb" load resolving after the real post-login load).
let requestToken = 0;

/**
 * Initial / post-login load. Uses initNexus, so a failure falls back to defaults
 * (correct at bootstrap — no prior good state to protect). Always wins over any
 * in-flight refetch via the request token.
 */
export const loadNexus = async (companyId?: string): Promise<void> => {
  currentCompanyId = companyId || ANON_COMPANY_ID;
  const token = ++requestToken;
  const data = await initNexus(currentCompanyId);
  if (token !== requestToken) return; // superseded by a newer load/refetch
  nexusStore.getState().setNexus(data, currentCompanyId);
  lastFetchedAt = Date.now();
};

/**
 * Mid-session refetch, guarded three ways:
 *  1. TTL floor  — skip if the last fetch is still fresh.
 *  2. Company    — skip while unauthenticated ("ffb"); the trx flag is irrelevant
 *                  until a real company is loaded, which also scopes refetches to
 *                  the authenticated (gated) part of the app without a path list.
 *  3. Failure    — refetchNexus returns null on error; we KEEP the current state.
 * Plus an in-flight lock and the out-of-order request token.
 */
export const maybeRefetchNexus = async (): Promise<void> => {
  if (inFlight) return;
  if (currentCompanyId === ANON_COMPANY_ID) return; // no authenticated company yet
  if (Date.now() - lastFetchedAt < getStaleTtlMs()) return; // still fresh

  inFlight = true;
  const token = ++requestToken;
  try {
    const data = await refetchNexus(currentCompanyId);
    if (token !== requestToken) return; // superseded → ignore stale response
    if (!data) return; // failure → keep current state, do NOT overwrite
    nexusStore.getState().setNexus(data, currentCompanyId);
    lastFetchedAt = Date.now();
  } finally {
    inFlight = false;
  }
};
